"use client";

import { useEffect } from "react";
import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import Loader from "@/components/Loader";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchOurJourneys } from "@/store/slices/landingSlice";

const CompanyStorySection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: ourJourneys, loading } = useAppSelector(
    (state) => state.landing.ourJourneys,
  );
  const isDark = mode === "dark";

  useEffect(() => {
    dispatch(fetchOurJourneys());
  }, [dispatch]);

  if (loading) {
    return (
      <section
        className={`py-24 flex items-center justify-center min-h-[600px] transition-colors duration-700 ${
          isDark
            ? "bg-[#0B0F19]"
            : "bg-gradient-to-b from-white via-slate-50 to-white"
        }`}
      >
        <Loader
          loading={loading}
          loadingStates={[
            { text: "Loading our journey" },
            { text: "Assembling story timeline" },
            { text: "Finalizing story content" },
          ]}
          mode={mode}
          className="min-h-[220px]"
        />
      </section>
    );
  }

  const journeyData =
    ourJourneys && ourJourneys.length > 0 ? ourJourneys[0] : null;
  if (!journeyData) return null;

  return (
    <section
      className={`relative overflow-hidden py-32 transition-colors duration-700 ${
        isDark
          ? "bg-[#0B0F19]"
          : "bg-gradient-to-b from-white via-slate-50 to-white"
      }`}
    >
      {/* ── Background (matches AboutHeroSection) ── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.05]" : "opacity-[0.04]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          className={`absolute -left-24 top-8 h-[420px] w-[420px] rounded-full blur-[110px] ${isDark ? "bg-[#8B31FF]/25" : "bg-[#8B31FF]/12"}`}
          animate={{ x: [0, 70, 0], y: [0, 25, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className={`absolute -right-20 bottom-0 h-[460px] w-[460px] rounded-full blur-[120px] ${isDark ? "bg-[#8B31FF]/18" : "bg-[#8B31FF]/10"}`}
          animate={{ x: [0, -80, 0], y: [0, 30, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ══════════════════════════════════════════
            SECTION HEADER
        ══════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-24"
        >
          {/* Glowing pill badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <span
              className={`relative inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold tracking-widest uppercase ${
                isDark
                  ? "bg-[#8B31FF]/20 text-[#8B31FF] border border-[#8B31FF]/40"
                  : "bg-[#8B31FF]/10 text-[#8B31FF] border border-[#8B31FF]/25"
              }`}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8B31FF] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#8B31FF]" />
              </span>
              {/* {journeyData.sectionTitle} */}
            </span>
          </div>

          <h2
            className={`text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-6 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#8B31FF] via-[#a855f7] to-[#8B31FF] bg-clip-text text-transparent">
                {journeyData.mainTitle}
              </span>
              {/* Underline glow */}
              <span className="absolute -bottom-1 left-0 right-0 h-[2.5px] rounded-full bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] opacity-60" />
            </span>
          </h2>

          <p
            className={`text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {journeyData.subtitle}
          </p>
        </motion.div>

        {/* ══════════════════════════════════════════
            STORY + VISUAL — Two column
        ══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-28">
          {/* LEFT — Story text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-5"
          >
            {journeyData.storyParagraphs.map((paragraph, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`text-base sm:text-lg leading-relaxed ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {paragraph}
              </motion.p>
            ))}

            {/* Professional Quote Card */}
            {journeyData.quoteBox?.quoteText && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                whileHover={{ y: -2 }}
                className={`relative mt-8 rounded-lg p-6 sm:p-7 border backdrop-blur-xl transition-all duration-300 shadow-sm ${
                  isDark
                    ? "bg-gradient-to-br from-white/[0.04] to-[#8B31FF]/[0.06] border-white/10 hover:border-[#8B31FF]/40 shadow-[0_4px_24px_rgba(0,0,0,0.2)]"
                    : "bg-gradient-to-br from-white to-purple-50/40 border-[#8B31FF]/20 shadow-[0_4px_20px_rgba(139,49,255,0.06)] hover:border-[#8B31FF]/40"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-[#8B31FF] to-[#6D28D9] flex items-center justify-center shadow-md shadow-[#8B31FF]/20">
                    <LucideIcons.Quote className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`italic text-base sm:text-lg font-medium leading-relaxed mb-4 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      &ldquo;{journeyData.quoteBox.quoteText}&rdquo;
                    </p>
                    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#8B31FF]/15">
                      <span className="font-bold text-sm text-[#8B31FF]">
                        {journeyData.quoteBox.quoteAuthor}
                      </span>
                      {journeyData.quoteBox.quoteDesignation && (
                        <>
                          <span
                            className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          >
                            •
                          </span>
                          <span
                            className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${
                              isDark
                                ? "bg-white/5 text-gray-300"
                                : "bg-purple-100/70 text-[#8B31FF]"
                            }`}
                          >
                            {journeyData.quoteBox.quoteDesignation}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* RIGHT — Animated visual card */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="relative flex flex-col gap-6"
          >
            {/* Main visual orb card */}
            <div
              className={`relative rounded-3xl overflow-hidden shadow-2xl border ${
                isDark ? "border-white/10" : "border-gray-100"
              }`}
            >
              {/* Gradient background */}
              <div className="relative aspect-[4/3] bg-gradient-to-br from-[#4c1d95] via-[#8B31FF] to-[#8B31FF] flex items-center justify-center overflow-hidden">
                {/* Concentric rings */}
                {[160, 220, 280].map((size, i) => (
                  <motion.div
                    key={i}
                    animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
                    transition={{
                      duration: 18 + i * 6,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    style={{ width: size, height: size }}
                    className="absolute rounded-full border border-white/10"
                  />
                ))}

                {/* Centre icon */}
                <div className="relative z-10 text-center text-white">
                  <motion.div
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="w-28 h-28 mx-auto mb-5 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-[0_0_40px_rgba(255,255,255,0.2)]"
                  >
                    {(() => {
                      const Icon =
                        (LucideIcons as Record<string, any>)[journeyData.visualSection.visualIcon] ||
                        LucideIcons.Rocket;
                      return (
                        <Icon className="w-14 h-14 text-white drop-shadow-lg" />
                      );
                    })()}
                  </motion.div>
                  <h3 className="text-4xl font-extrabold tracking-tight drop-shadow-lg">
                    {journeyData.visualSection.visualYears}
                  </h3>
                  <p className="text-base text-white/80 mt-1 tracking-wide">
                    {journeyData.visualSection.visualSubtitle}
                  </p>
                </div>

                {/* Corner shimmer */}
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              </div>
            </div>

            {/* Floating stat cards — row below */}
            <div className="grid grid-cols-2 gap-4">
              {journeyData.visualSection.floatingStats.map((stat, index) => {
                // @ts-ignore
                const StatIcon = LucideIcons[stat.icon] || LucideIcons.Trophy;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.15, duration: 0.6 }}
                    whileHover={{ y: -4, scale: 1.02 }}
                    className={`flex items-center gap-4 rounded-lg p-5 border shadow-lg transition-all duration-300 ${
                      isDark
                        ? "bg-[#0F1626] border-white/10 hover:border-[#8B31FF]/50"
                        : "bg-white border-gray-100 hover:border-[#8B31FF]/30 hover:shadow-[#8B31FF]/10"
                    }`}
                  >
                    <div className="w-12 h-12 shrink-0 rounded-lg bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] flex items-center justify-center shadow-md">
                      <StatIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`font-bold text-base truncate ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {stat.title}
                      </p>
                      <p
                        className={`text-xs truncate ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {stat.subtitle}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* ══════════════════════════════════════════
            VERTICAL ALTERNATING TIMELINE
        ══════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span
            className={`text-sm font-semibold uppercase tracking-widest ${
              isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"
            }`}
          >
            Our Milestones
          </span>
          <h3
            className={`text-3xl md:text-4xl font-bold mt-2 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Moments That Shaped Us
          </h3>
        </motion.div>

        <div className="relative">
          {/* Centre vertical line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 hidden md:block">
            <div
              className={`h-full w-full ${
                isDark
                  ? "bg-gradient-to-b from-transparent via-[#8B31FF]/40 to-transparent"
                  : "bg-gradient-to-b from-transparent via-[#8B31FF]/25 to-transparent"
              }`}
            />
          </div>

          <div className="space-y-12 md:space-y-0">
            {journeyData.milestones.map((milestone, index) => {
              // @ts-ignore
              const Icon = LucideIcons[milestone.icon] || LucideIcons.Lightbulb;
              const isLeft = index % 2 === 0;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: index * 0.1, duration: 0.7 }}
                  className={`relative flex items-center md:mb-16 ${
                    isLeft ? "md:flex-row" : "md:flex-row-reverse"
                  } flex-col`}
                >
                  {/* Card */}
                  <div
                    className={`md:w-[calc(50%-3.5rem)] w-full ${isLeft ? "md:pr-8 md:text-right" : "md:pl-8 md:text-left"} text-left`}
                  >
                    <motion.div
                      whileHover={{ y: -4 }}
                      className={`rounded-lg p-7 border shadow-lg transition-all duration-300 ${
                        isDark
                          ? "bg-[#0D1220] border-white/8 hover:border-[#8B31FF]/40 hover:shadow-[#8B31FF]/10"
                          : "bg-white border-gray-100 hover:border-[#8B31FF]/30 hover:shadow-lg hover:shadow-[#8B31FF]/5"
                      }`}
                    >
                      {/* Year pill */}
                      <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] text-white text-sm font-bold shadow mb-4">
                        {milestone.year}
                      </span>
                      <h4
                        className={`text-xl font-bold mb-2 ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {milestone.title}
                      </h4>
                      <p
                        className={`text-sm leading-relaxed ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {milestone.description}
                      </p>
                    </motion.div>
                  </div>

                  {/* Centre node */}
                  <div className="hidden md:flex shrink-0 w-14 items-center justify-center relative z-10">
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                      className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8B31FF] to-[#8B31FF] flex items-center justify-center shadow-lg shadow-[#8B31FF]/30 border-4 border-white dark:border-[#060B1A]"
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </motion.div>
                  </div>

                  {/* Spacer for opposite side */}
                  <div className="md:w-[calc(50%-3.5rem)] hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyStorySection;
